import type { BuiltinWorkflowTag } from "~/models/itandi_task/Workflow";
import {
	BUILTIN_WORKFLOW_TAG_SYSTEM_KEY,
	hasBuiltinWorkflowTag
} from "~/models/itandi_task/Workflow";

export const WORKFLOW_RESOURCE_TYPE = {
	ESTIMATION: "estimation",
	OWNER_BILLING: "owner_billing"
} as const;

export type WorkflowResourceType =
	(typeof WORKFLOW_RESOURCE_TYPE)[keyof typeof WORKFLOW_RESOURCE_TYPE];

export const WORKFLOW_RESOURCE_TYPE_LABEL = {
	[WORKFLOW_RESOURCE_TYPE.ESTIMATION]: "見積り画面",
	[WORKFLOW_RESOURCE_TYPE.OWNER_BILLING]: "オーナー請求画面"
} as const satisfies Record<WorkflowResourceType, string>;

export const WORKFLOW_LOCK_TARGET_LABEL = {
	[WORKFLOW_RESOURCE_TYPE.ESTIMATION]: "見積り",
	[WORKFLOW_RESOURCE_TYPE.OWNER_BILLING]: "オーナーへの見積り・請求・精算"
} as const satisfies Record<WorkflowResourceType, string>;

export type CompanyWorkflowSettingWorkflowDefinition = {
	builtinWorkflowTags: BuiltinWorkflowTag[];
	description: string | null;
	id: number;
	name: string;
	workflowUuid: string;
};

export type CompanyWorkflowSetting = {
	description: string | null;
	id: number;
	resourceType: WorkflowResourceType;
	title: string;
	workflowDefinitions: CompanyWorkflowSettingWorkflowDefinition[];
};

// 利用画面区分は会社ごとに一意のため、該当する設定は最大1件
const findCompanyWorkflowSetting = (
	companyWorkflowSettings: CompanyWorkflowSetting[] | null,
	targetScreen: WorkflowResourceType
): CompanyWorkflowSetting | null =>
	companyWorkflowSettings?.find(
		(setting) => setting.resourceType === targetScreen
	) ?? null;

export const canStartWorkflow = (
	companyWorkflowSettings: CompanyWorkflowSetting[] | null,
	targetScreen: WorkflowResourceType
): boolean =>
	findCompanyWorkflowSetting(companyWorkflowSettings, targetScreen) !== null;

// 利用画面区分は会社ごとに1件のみ設定できるため、未設定の利用画面区分だけを返す
export const getSelectableResourceTypes = (
	companyWorkflowSettings: CompanyWorkflowSetting[]
): WorkflowResourceType[] => {
	const configuredResourceTypes = new Set(
		companyWorkflowSettings.map((setting) => setting.resourceType)
	);
	return Object.values(WORKFLOW_RESOURCE_TYPE).filter(
		(resourceType) => !configuredResourceTypes.has(resourceType)
	);
};

export const isApprovalRequiredWorkflow = (
	workflowDefinitions: readonly CompanyWorkflowSettingWorkflowDefinition[],
	workflowUuid: string
): boolean => {
	const workflowDefinition = workflowDefinitions.find(
		(definition) => definition.workflowUuid === workflowUuid
	);
	return (
		workflowDefinition !== undefined &&
		hasBuiltinWorkflowTag(
			workflowDefinition.builtinWorkflowTags,
			BUILTIN_WORKFLOW_TAG_SYSTEM_KEY.APPROVAL_REQUIRED
		)
	);
};
